import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, MapPin, Clock, DollarSign, Tag, Filter, 
  Car, Package, ShoppingCart, Truck, User, Star,
  ChevronRight, Loader2, MessageCircle, Check, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { taskAPI, bidAPI } from '../lib/api';
import { toast } from 'sonner';
import NotificationBell from '../components/NotificationBell';
import ChatButton from '../components/ChatButton';
import { devError } from '../lib/devLog';

const CATEGORIES = [
  { value: 'ride', label: 'Ride', icon: Car, color: 'bg-blue-100 text-blue-600' },
  { value: 'delivery', label: 'Delivery', icon: Package, color: 'bg-green-100 text-green-600' },
  { value: 'errand', label: 'Errand', icon: ShoppingCart, color: 'bg-purple-100 text-purple-600' },
  { value: 'transportation', label: 'Transportation', icon: Truck, color: 'bg-orange-100 text-orange-600' },
];

const TaskMarketplace = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [myTasks, setMyTasks] = useState([]);
  const [myBids, setMyBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showBidDialog, setShowBidDialog] = useState(false);
  const [showTaskDetail, setShowTaskDetail] = useState(false);
  const [taskBids, setTaskBids] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    category: '',
    location_address: '',
    budget_min: '',
    budget_max: '',
    deadline: '',
  });
  
  const [newBid, setNewBid] = useState({
    amount: '',
    message: '',
    estimated_time: '',
  });

  useEffect(() => {
    fetchData();
  }, [categoryFilter]);

  const fetchData = async () => {
    try {
      const params = { status: 'open' };
      if (categoryFilter !== 'all') {
        params.category = categoryFilter;
      }
      
      const [tasksRes, myTasksRes, myBidsRes] = await Promise.all([
        taskAPI.getAll(params),
        taskAPI.getMy(),
        bidAPI.getMy(),
      ]);
      setTasks(tasksRes.data);
      setMyTasks(myTasksRes.data);
      setMyBids(myBidsRes.data);
    } catch (error) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await taskAPI.create({
        ...newTask,
        budget_min: newTask.budget_min ? parseFloat(newTask.budget_min) : null,
        budget_max: newTask.budget_max ? parseFloat(newTask.budget_max) : null,
      });
      toast.success('Task posted successfully!');
      setShowCreateTask(false);
      setNewTask({
        title: '',
        description: '',
        category: '',
        location_address: '',
        budget_min: '',
        budget_max: '',
        deadline: '',
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create task');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePlaceBid = async (e) => {
    e.preventDefault();
    if (!selectedTask) return;
    
    setSubmitting(true);
    try {
      await bidAPI.create({
        task_id: selectedTask.id,
        amount: parseFloat(newBid.amount),
        message: newBid.message || null,
        estimated_time: newBid.estimated_time || null,
      });
      toast.success('Bid placed successfully!');
      setShowBidDialog(false);
      setNewBid({ amount: '', message: '', estimated_time: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to place bid');
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewTask = async (task) => {
    setSelectedTask(task);
    try {
      const res = await taskAPI.getBids(task.id);
      setTaskBids(res.data);
    } catch (error) {
      devError('Failed to fetch bids:', error);
    }
    setShowTaskDetail(true);
  };

  const handleAcceptBid = async (bidId) => {
    setSubmitting(true);
    try {
      await bidAPI.accept(bidId);
      toast.success('Bid accepted!');
      setShowTaskDetail(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept bid');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      await taskAPI.complete(taskId);
      toast.success('Task marked as completed!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete task');
    }
  };

  const getCategoryInfo = (category) => {
    return CATEGORIES.find(c => c.value === category) || CATEGORIES[0];
  };

  const formatBudget = (task) => {
    if (task.budget_min && task.budget_max) {
      return `$${task.budget_min} - $${task.budget_max}`;
    } else if (task.budget_min) {
      return `From $${task.budget_min}`;
    } else if (task.budget_max) {
      return `Up to $${task.budget_max}`;
    }
    return 'Open budget';
  };

  const alreadyBid = (taskId) => {
    return myBids.some(b => b.task_id === taskId);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} className="text-zinc-500 hover:text-zinc-700">
              ← Back
            </button>
            <h1 className="font-bold text-xl text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Task Marketplace
            </h1>
          </div>
          
          <div className="flex items-center gap-3">
            <ChatButton />
            <NotificationBell />
            <Button
              onClick={() => setShowCreateTask(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="post-task-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Post Task
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">
        <Tabs defaultValue="browse" className="space-y-6">
          <TabsList className="bg-white border border-zinc-200">
            <TabsTrigger value="browse">Browse Tasks</TabsTrigger>
            <TabsTrigger value="my-tasks">My Tasks ({myTasks.length})</TabsTrigger>
            <TabsTrigger value="my-bids">My Bids ({myBids.length})</TabsTrigger>
          </TabsList>

          {/* Browse Tasks */}
          <TabsContent value="browse" className="space-y-4">
            {/* Category Filter */}
            <div className="flex gap-2 overflow-x-auto pb-2">
              <Button
                variant={categoryFilter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setCategoryFilter('all')}
                className={categoryFilter === 'all' ? 'bg-orange-500 hover:bg-orange-600' : ''}
              >
                All
              </Button>
              {CATEGORIES.map((cat) => {
                const Icon = cat.icon;
                return (
                  <Button
                    key={cat.value}
                    variant={categoryFilter === cat.value ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setCategoryFilter(cat.value)}
                    className={categoryFilter === cat.value ? 'bg-orange-500 hover:bg-orange-600' : ''}
                  >
                    <Icon className="w-4 h-4 mr-1" />
                    {cat.label}
                  </Button>
                );
              })}
            </div>

            {/* Task List */}
            {tasks.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-zinc-200">
                <Tag className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
                <p className="text-zinc-600">No tasks found</p>
                <p className="text-sm text-zinc-500 mt-1">Be the first to post a task!</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {tasks.map((task) => {
                  const catInfo = getCategoryInfo(task.category);
                  const Icon = catInfo.icon;
                  const hasBid = alreadyBid(task.id);
                  
                  return (
                    <div
                      key={task.id}
                      className="bg-white border border-zinc-200 rounded-xl p-5 hover:border-orange-200 transition-colors"
                      data-testid={`task-${task.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex gap-4">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${catInfo.color}`}>
                            <Icon className="w-6 h-6" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-zinc-900">{task.title}</h3>
                            <p className="text-sm text-zinc-600 mt-1 line-clamp-2">{task.description}</p>
                            <div className="flex flex-wrap gap-3 mt-3 text-sm text-zinc-500">
                              <span className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                {task.location_address}
                              </span>
                              <span className="flex items-center gap-1">
                                <DollarSign className="w-4 h-4" />
                                {formatBudget(task)}
                              </span>
                              <span className="flex items-center gap-1">
                                <User className="w-4 h-4" />
                                {task.user_name}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="text-right">
                          <p className="text-sm text-zinc-500">{task.bid_count} bids</p>
                          {task.user_id !== user?.id && (
                            <Button
                              onClick={() => {
                                setSelectedTask(task);
                                setShowBidDialog(true);
                              }}
                              disabled={hasBid}
                              className={hasBid ? 'bg-zinc-200 text-zinc-500' : 'bg-orange-500 hover:bg-orange-600 text-white mt-2'}
                              size="sm"
                              data-testid={`bid-btn-${task.id}`}
                            >
                              {hasBid ? 'Bid Placed' : 'Place Bid'}
                            </Button>
                          )}
                          {task.user_id === user?.id && (
                            <Button
                              onClick={() => handleViewTask(task)}
                              variant="outline"
                              size="sm"
                              className="mt-2"
                            >
                              View Bids
                              <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* My Tasks */}
          <TabsContent value="my-tasks" className="space-y-4">
            {myTasks.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-zinc-200">
                <Tag className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
                <p className="text-zinc-600">You haven't posted any tasks</p>
                <Button
                  onClick={() => setShowCreateTask(true)}
                  className="bg-orange-500 hover:bg-orange-600 text-white mt-4"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Post Your First Task
                </Button>
              </div>
            ) : (
              <div className="grid gap-4">
                {myTasks.map((task) => {
                  const catInfo = getCategoryInfo(task.category);
                  const Icon = catInfo.icon;
                  
                  return (
                    <div
                      key={task.id}
                      className="bg-white border border-zinc-200 rounded-xl p-5"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex gap-4">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${catInfo.color}`}>
                            <Icon className="w-6 h-6" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-zinc-900">{task.title}</h3>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                task.status === 'open' ? 'bg-green-100 text-green-700' :
                                task.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                                task.status === 'completed' ? 'bg-zinc-100 text-zinc-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {task.status.replace('_', ' ')}
                              </span>
                            </div>
                            <p className="text-sm text-zinc-500 mt-1">
                              {task.bid_count} bids • {formatBudget(task)}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex gap-2">
                          {task.status === 'open' && (
                            <Button
                              onClick={() => handleViewTask(task)}
                              variant="outline"
                              size="sm"
                            >
                              View Bids
                            </Button>
                          )}
                          {task.status === 'in_progress' && (
                            <Button
                              onClick={() => handleCompleteTask(task.id)}
                              className="bg-green-500 hover:bg-green-600 text-white"
                              size="sm"
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Complete
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* My Bids */}
          <TabsContent value="my-bids" className="space-y-4">
            {myBids.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-zinc-200">
                <DollarSign className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
                <p className="text-zinc-600">You haven't placed any bids</p>
                <p className="text-sm text-zinc-500 mt-1">Browse tasks and place your first bid!</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {myBids.map((bid) => (
                  <div
                    key={bid.id}
                    className="bg-white border border-zinc-200 rounded-xl p-5"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-zinc-900">Task #{bid.task_id.slice(0, 8)}</p>
                        <p className="text-sm text-zinc-500 mt-1">
                          Your bid: ${bid.amount.toFixed(2)}
                          {bid.message && ` • "${bid.message}"`}
                        </p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        bid.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                        bid.status === 'accepted' ? 'bg-green-100 text-green-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {bid.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Create Task Dialog */}
      <Dialog open={showCreateTask} onOpenChange={setShowCreateTask}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Post a Task</DialogTitle>
            <DialogDescription>
              Describe what you need help with
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreateTask} className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                placeholder="e.g., Need a ride to the airport"
                value={newTask.title}
                onChange={(e) => setNewTask(prev => ({ ...prev, title: e.target.value }))}
                required
                data-testid="task-title-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select
                value={newTask.category}
                onValueChange={(value) => setNewTask(prev => ({ ...prev, category: value }))}
              >
                <SelectTrigger data-testid="task-category-select">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Describe your task in detail..."
                value={newTask.description}
                onChange={(e) => setNewTask(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                required
                data-testid="task-description-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                placeholder="e.g., 123 Main St, Mascoutah, IL"
                value={newTask.location_address}
                onChange={(e) => setNewTask(prev => ({ ...prev, location_address: e.target.value }))}
                required
                data-testid="task-location-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="budget_min">Min Budget ($)</Label>
                <Input
                  id="budget_min"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={newTask.budget_min}
                  onChange={(e) => setNewTask(prev => ({ ...prev, budget_min: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="budget_max">Max Budget ($)</Label>
                <Input
                  id="budget_max"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={newTask.budget_max}
                  onChange={(e) => setNewTask(prev => ({ ...prev, budget_max: e.target.value }))}
                />
              </div>
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowCreateTask(false)} className="flex-1">
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                disabled={submitting || !newTask.title || !newTask.category || !newTask.description || !newTask.location_address}
                data-testid="submit-task-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Post Task'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Place Bid Dialog */}
      <Dialog open={showBidDialog} onOpenChange={setShowBidDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Place a Bid</DialogTitle>
            <DialogDescription>
              {selectedTask?.title}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handlePlaceBid} className="space-y-4 pt-2">
            <div className="bg-zinc-50 rounded-lg p-3">
              <p className="text-sm text-zinc-600">Budget: {selectedTask && formatBudget(selectedTask)}</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="bid_amount">Your Bid ($)</Label>
              <Input
                id="bid_amount"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={newBid.amount}
                onChange={(e) => setNewBid(prev => ({ ...prev, amount: e.target.value }))}
                required
                data-testid="bid-amount-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="bid_message">Message (optional)</Label>
              <Textarea
                id="bid_message"
                placeholder="Why should they choose you?"
                value={newBid.message}
                onChange={(e) => setNewBid(prev => ({ ...prev, message: e.target.value }))}
                rows={2}
              />
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowBidDialog(false)} className="flex-1">
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                disabled={submitting || !newBid.amount}
                data-testid="submit-bid-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Place Bid'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Task Detail / View Bids Dialog */}
      <Dialog open={showTaskDetail} onOpenChange={setShowTaskDetail}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              {selectedTask?.title}
            </DialogTitle>
            <DialogDescription>
              {taskBids.length} bids received
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 pt-2">
            {taskBids.length === 0 ? (
              <div className="text-center py-8">
                <DollarSign className="w-10 h-10 text-zinc-300 mx-auto mb-2" />
                <p className="text-zinc-500">No bids yet</p>
              </div>
            ) : (
              taskBids.map((bid) => (
                <div
                  key={bid.id}
                  className="border border-zinc-200 rounded-xl p-4"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-zinc-100 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-zinc-600" />
                      </div>
                      <div>
                        <p className="font-medium text-zinc-900">{bid.bidder_name}</p>
                        <div className="flex items-center gap-2 text-sm text-zinc-500">
                          <span className="capitalize">{bid.bidder_role}</span>
                          {bid.bidder_rating && (
                            <>
                              <span>•</span>
                              <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                              <span>{bid.bidder_rating}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <p className="text-xl font-bold text-zinc-900">${bid.amount.toFixed(2)}</p>
                  </div>
                  
                  {bid.message && (
                    <p className="text-sm text-zinc-600 mt-3 bg-zinc-50 p-3 rounded-lg">
                      "{bid.message}"
                    </p>
                  )}
                  
                  {selectedTask?.user_id === user?.id && bid.status === 'pending' && selectedTask?.status === 'open' && (
                    <div className="flex gap-2 mt-4">
                      <Button
                        onClick={() => handleAcceptBid(bid.id)}
                        className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                        disabled={submitting}
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Accept Bid
                      </Button>
                    </div>
                  )}
                  
                  {bid.status !== 'pending' && (
                    <div className="mt-3">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        bid.status === 'accepted' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {bid.status}
                      </span>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TaskMarketplace;
